/**
 * Hook for managing admin report filtering, fetching, and state
 *
 * Composes the ticket-list domain hooks and coordinates them:
 * - useReportFilters: HRO, KPI, and Organization filter state
 * - useTicketData: ticket fetching, pagination, and view counts
 * - useTicketSearch: debounced embedding search
 * - useReportStatusCache: commented/closed/reopened tracking from cache
 * - useBulkTicketActions: select mode and bulk close/assign
 *
 * This hook owns the time filter, ticket view, and toggle state, plus the
 * effects that tie filter/view changes to report regeneration.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useIsFocused } from '@react-navigation/native';

import Util from '../util/util.js';
import { useErrorState, showSuccess } from '../util/error';
import { trackEngagementAsync } from '../util/engagementTracker';
import { TIME_OPTIONS, TICKET_VIEW_OPTIONS, TIME_CONSTRAINED_VIEWS } from '../util/reports';

import useReportFilters from './useReportFilters';
import useReportStatusCache from './useReportStatusCache';
import useTicketData from './useTicketData';
import useTicketSearch from './useTicketSearch';
import useBulkTicketActions from './useBulkTicketActions';
import { t } from '../i18n';

// Re-export for backward compatibility
export { TIME_OPTIONS, TICKET_VIEW_OPTIONS };

export default function useAdminReports(organization, filterOrg, initialView) {
  const isFocused = useIsFocused();

  // Error state
  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'AdminReports',
    showToasts: true,
  });

  // Use extracted filters hook
  const filtersHook = useReportFilters(organization, filterOrg);

  // Time filter state
  const [checked, setChecked] = useState(-1);
  // Custom date range filter ('YYYY-MM-DD' strings, empty = unbounded)
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');

  // Toggle filters
  const [includePreviousReports, setIncludePreviousReports] = useState(false);
  const [displayFilter, setDisplayFilter] = useState(false);

  // Ticket view preset (combines status + assignment)
  const [ticketView, setTicketView] = useState(initialView || 'myActive');
  const hasAutoSwitchedView = useRef(false);

  const ticketViewRef = useRef(ticketView);
  ticketViewRef.current = ticketView;

  // View-specific toggles
  // includeAssigned: For "All Open" - show assigned tickets too (persists across view changes)
  const [includeAssigned, setIncludeAssigned] = useState(false);
  // showOlderTickets: For time-constrained views - show tickets older than 30 days (resets on view change)
  const [showOlderTickets, setShowOlderTickets] = useState(false);

  // Report status tracking (from cache)
  const statusCache = useReportStatusCache();
  const { updateCache } = statusCache;

  // Ticket fetching, pagination, and counts
  const ticketData = useTicketData(organization, {
    filtersHook,
    ticketView,
    ticketViewRef,
    includePreviousReports,
    includeAssigned,
    showOlderTickets,
    dateRangeStart,
    dateRangeEnd,
    onInitialFetch: statusCache.clearStatusFlags,
    handleError,
  });
  const { reports, reportsLoading, ticketCounts, fetchTickets, fetchTicketCounts } = ticketData;

  /**
   * Toggle functions
   */
  const toggleIncludePreviousReports = useCallback(() => {
    setIncludePreviousReports((prev) => !prev);
  }, []);

  const toggleDisplayFilter = useCallback(() => {
    setDisplayFilter((prev) => !prev);
  }, []);

  /**
   * Generate reports based on time filter (legacy - for time-based queries)
   */
  const generateReports = useCallback(async (timeFilter) => {
    // Use the new tickets endpoint for ticket views
    // This is much more efficient as it uses GSIs instead of scanning + filtering
    return fetchTickets();
  }, [fetchTickets]);

  // Debounced search
  const search = useTicketSearch(organization, {
    filtersHook,
    ticketView,
    includeAssigned,
    showOlderTickets,
    includePreviousReports,
    dateRangeStart,
    dateRangeEnd,
    checked,
    generateReports,
    setReports: ticketData.setReports,
    setReportsLoading: ticketData.setReportsLoading,
    handleError,
  });
  const { searchText, searchReports } = search;

  /**
   * Refresh reports and counts
   */
  const refresh = useCallback(() => {
    if (checked === -1) return; // Not initialized yet
    fetchTicketCounts(); // Refresh counts
    if (searchText?.trim()) {
      searchReports();
    } else {
      generateReports(checked);
    }
  }, [searchText, searchReports, generateReports, checked, fetchTicketCounts]);

  // Bulk operations
  const bulk = useBulkTicketActions(organization, { reports, refresh, handleError });

  /**
   * Email reports as a formatted Excel spreadsheet
   * (the /items/emailCSV route path is kept for deployed-client compatibility)
   */
  const emailReportAsExcel = useCallback(async (reportsToEmail) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await Util.post('Resty', '/items/emailCSV', { body: { repsToEmail: reportsToEmail, timezone } });
      showSuccess(t('admin:filterHome.excelSent'));

      // Track export for surprise badges — keep the historical 'csv_exported'
      // key so existing badge progress still counts
      trackEngagementAsync('csv_exported');
    } catch (err) {
      handleError(err, { userMessage: t('admin:filterHome.excelFailed') });
    }
  }, [handleError]);

  // Effects — initial load: fetch counts first, then set checked to trigger reports.
  // checked stays at -1 until counts resolve (or fail), gating all report-fetching effects.
  const [countsFetched, setCountsFetched] = useState(false);
  useEffect(() => {
    fetchTicketCounts().finally(() => setCountsFetched(true));
  }, [fetchTicketCounts]);

  // Once counts are loaded (or failed), auto-switch view if needed and initialize checked.
  useEffect(() => {
    if (countsFetched && checked === -1) {
      if (ticketCounts && !hasAutoSwitchedView.current && ticketView === 'myActive' && ticketCounts.myActive === 0) {
        hasAutoSwitchedView.current = true;
        ticketViewRef.current = 'allOpen';
        setTicketView('allOpen');
      }
      setChecked(1);
    }
  }, [countsFetched, ticketCounts, checked, ticketView]);

  // Auto-switch on subsequent count refreshes (e.g. org filter change)
  useEffect(() => {
    if (!hasAutoSwitchedView.current && checked !== -1 && ticketCounts && ticketView === 'myActive' && ticketCounts.myActive === 0) {
      hasAutoSwitchedView.current = true;
      ticketViewRef.current = 'allOpen';
      setTicketView('allOpen');
    }
  }, [ticketCounts, ticketView, checked]);

  useEffect(() => {
    if (checked !== -1) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, includePreviousReports, dateRangeStart, dateRangeEnd]);

  useEffect(() => {
    if (isFocused) {
      updateCache(reports);
    }
  }, [isFocused, reports, updateCache]);

  // Filter effects - trigger report regeneration when filters change.
  // Triggers when dropdown closes OR when value changes while closed.
  // Use refresh() (not generateReports) so the active searchTerm is preserved
  // when the user adjusts a filter after running a search.
  useEffect(() => {
    if (!filtersHook.HROFilterOpen && filtersHook.checkHROFilterChanged()) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHook.HROFilterOpen, filtersHook.HROFilterValue]);

  useEffect(() => {
    if (!filtersHook.KPIFilterOpen && filtersHook.checkKPIFilterChanged()) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHook.KPIFilterOpen, filtersHook.KPIFilterValue]);

  useEffect(() => {
    if (checked !== -1 && !filtersHook.OrgFilterOpen && filtersHook.checkOrgFilterChanged()) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHook.OrgFilterOpen, filtersHook.OrgFilterValue]);

  useEffect(() => {
    if (reports?.length > 0) {
      updateCache(reports);
    }
  }, [reports, updateCache]);

  // Reset showOlderTickets and exit select mode when ticket view changes
  useEffect(() => {
    setShowOlderTickets(false);
    bulk.exitSelectMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketView]);

  // Ticketing filter effects - regenerate reports when ticket view changes
  useEffect(() => {
    if (checked !== -1) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketView]);

  // Regenerate when toggles change
  useEffect(() => {
    if (checked !== -1) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAssigned]);

  // Regenerate and update counts when showOlderTickets changes.
  // Use refresh() so an active searchTerm is preserved across the toggle.
  useEffect(() => {
    if (checked !== -1) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOlderTickets]);

  return {
    // Error state
    error,
    hasError,
    clearError,

    // Report data
    reports,
    reportsLoading,
    ticketCounts,

    // Pagination (infinite scroll)
    loadingMore: ticketData.loadingMore,
    hasMore: ticketData.hasMore,
    loadMore: ticketData.loadMore,

    // Time filter
    checked,
    setChecked,

    // Search
    searchText,
    setSearchText: search.setSearchText,
    searchReports,
    resetSearch: search.resetSearch,

    // Toggles
    includePreviousReports,
    toggleIncludePreviousReports,
    displayFilter,
    toggleDisplayFilter,

    // Ticketing filters
    ticketView,
    setTicketView,
    // View-specific toggles
    includeAssigned,
    setIncludeAssigned,
    showOlderTickets,
    setShowOlderTickets,
    // Check if current view has time constraint
    isTimeConstrained: TIME_CONSTRAINED_VIEWS.includes(ticketView),

    // HRO filter (from filters hook)
    HROFilterOpen: filtersHook.HROFilterOpen,
    setHROFilterOpen: filtersHook.setHROFilterOpen,
    HROFilterValue: filtersHook.HROFilterValue,
    setHROFilterValue: filtersHook.setHROFilterValue,
    HROFilter: filtersHook.HROFilter,
    setHROFilter: filtersHook.setHROFilter,

    // KPI filter (from filters hook)
    KPIFilterOpen: filtersHook.KPIFilterOpen,
    setKPIFilterOpen: filtersHook.setKPIFilterOpen,
    KPIFilterValue: filtersHook.KPIFilterValue,
    setKPIFilterValue: filtersHook.setKPIFilterValue,
    KPIFilter: filtersHook.KPIFilter,
    setKPIFilter: filtersHook.setKPIFilter,

    // Organization filter (from filters hook)
    OrgFilterOpen: filtersHook.OrgFilterOpen,
    setOrgFilterOpen: filtersHook.setOrgFilterOpen,
    OrgFilterValue: filtersHook.OrgFilterValue,
    setOrgFilterValue: filtersHook.setOrgFilterValue,
    OrgFilter: filtersHook.OrgFilter,
    setOrgFilter: filtersHook.setOrgFilter,
    loadingOrg: filtersHook.loadingOrg,
    validOrgs: filtersHook.validOrgs,

    // Actions
    refresh,
    emailReportAsExcel,
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,

    // Status helpers
    hasComments: statusCache.hasComments,
    isClosed: statusCache.isClosed,

    // Bulk operations
    selectMode: bulk.selectMode,
    toggleSelectMode: bulk.toggleSelectMode,
    selectedUuids: bulk.selectedUuids,
    toggleSelect: bulk.toggleSelect,
    selectAll: bulk.selectAll,
    clearSelection: bulk.clearSelection,
    analysts: bulk.analysts,
    bulkLoading: bulk.bulkLoading,
    bulkMessage: bulk.bulkMessage,
    bulkClose: bulk.bulkClose,
    bulkAssign: bulk.bulkAssign,
  };
}
